import useNavigationStore from '@src/stores/navigationStore';

import styles from './Documents.module.scss';

import BlockTitle from '@src/components/_shared/UI/BlockTitle';
import Container from '@src/components/_shared/Container';
import Subtitle from '@src/components/_shared/UI/Subtitle';
import ValidateButton from '@src/components/_shared/UI/ValidateButton';
import { useMutation, useQuery } from '@tanstack/react-query';
import { clientFolderUpdate, getAllDocumentsData } from '@src/api/Registration';
import Loader from '@src/components/_shared/UI/Loader';
import { DocumentType } from '@src/utilities/types';
import useRegistrationStore from '@src/stores/registrationStore';
import { handleApiError } from '@src/utilities/functions';
import InfoBlueBox from '@src/components/_shared/InfoBlueBox';

export default function Documents() {
    const { setBlock } = useNavigationStore();
    const { registration } = useRegistrationStore();

    const { data, isLoading } = useQuery({ 
        queryKey: ["documentsStatus"],
        queryFn: () => getAllDocumentsData(registration.partner_informations.is_partner ? registration.demand_id : undefined),
    });

    const { mutate, isPending } = useMutation({
        mutationFn: () => clientFolderUpdate({is_psi: registration.partner_informations.is_partner, person_id: registration.person_id, demand_id: registration.demand_id, brand: registration.partner_informations.brand.slug}),
        onSuccess: () => {
            const hasFrontId = data?.documents?.some((doc: { type: string }) => doc.type === DocumentType.front);
            const hasBackId = data?.documents?.some((doc: { type: string }) => doc.type === DocumentType.back);
            const hasAddress = data?.documents?.some((doc: { type: string }) => doc.type === DocumentType.address);
    
            if (hasFrontId && hasBackId && hasAddress) {
                setBlock('CompanyDocuments');
            } else if (hasFrontId || hasBackId) {
                setBlock('ProofOfAddress');
            } else {
                setBlock('Identity');
            }
        },
        onError: (error: any) => handleApiError(error)
    });

    if (isLoading) {
        return(
            <Container>
                <Loader type='SPINNER' />
            </Container>
        )
    }

    return (
        <Container>
            <BlockTitle title="Pièces justificatives du représentant légal" />
            <Subtitle content="Avant de finaliser votre contrat, nous devons vérifier votre identité. Nous vous demanderons également une photo ou un scan des documents suivants :" />
            
            <div className={styles.contentContainer}>
                <p className={styles.content}>Une <span style={{ fontWeight: '600', color: 'rgba(51, 51, 51, 1)' }}>pièce d’identité</span> (recto/verso) :</p>
                <ul className={styles.list}>
                    <li>Carte d'identité</li>
                    <li>Passeport (copies des pages faisant apparaître le numéro de passeport, les noms, prénoms et signature)</li>
                    <li>Carte de résident</li>
                    <li>Permis de conduire français (uniquement les nouveaux permis avec puce)</li>
                </ul>
                <p className={styles.content}>Un <span style={{ fontWeight: '600', color: 'rgba(51, 51, 51, 1)'}}>justificatif de domicile</span> : Facture ou attestation de contrat d'électricité, d'eau, de gaz ou de fournisseur d'accès Internet. Ce justificatif doit être à votre nom (nom et prénom) et dater de moins de 3 mois.</p>
            </div>
            {registration.partner_informations.is_partner ?
                <InfoBlueBox
                    boxTitle="Important"
                    description={`En cliquant sur le bouton "Continuer", un email sera automatiquement envoyé au client. Celui-ci lui permettra de reprendre la main sur son parcours afin de téléverser les documents requis et de procéder à la signature électronique.`}
                />
                : null}
            <ValidateButton title="Continuer" onClick={mutate} disabled={isPending} onLoading={isPending} />
        </Container>
    );
}
